<script setup lang="ts">
import { type LoginConfirmProps, LoginPwdTfaSmsResend, LoginPwdTfaSmsVerify } from '@/api/login'
import { useUserInfo } from '@mp/hooks'
import type { FormInstance, FormRules } from 'element-plus'
import { nextTick, reactive, ref } from 'vue'
import type { SpInputVerificationCodeInstance } from '@sp/pc-ui'
import { useMutation } from '@tanstack/vue-query'
import { logon } from '@mp/api/gateway/UserControler.js'
import { getDeviceCode } from '@mp/utils'
import { ZF_NO } from '@mp/constant'

const props = defineProps<{
  data: LoginConfirmProps
}>()

const { isAdmin } = useUserInfo()
const visible = ref(false)
const formRef = ref<FormInstance>()
const smsInputRef = ref<SpInputVerificationCodeInstance>()
const formModel = reactive<{
  code: string | undefined
}>({
  code: undefined,
})

const formRules = reactive<FormRules>({
  code: [{ required: true, message: '请输入短信验证码', trigger: 'blur' }],
})

// #重新发送验证码
const { mutateAsync: mutateResendSms, data: resendSmsData } = useMutation({
  mutationFn: LoginPwdTfaSmsResend,
})

async function handleClickSend(countdown: () => void) {
  await mutateResendSms({
    smsResendTicket: resendSmsData.value?.smsResendTicket || props.data?.smsResendTicket,
  }).then(countdown).catch(() => undefined)
}

// #登录
const { mutateAsync: mutateLogonAsync } = useMutation({
  mutationFn: logon,
  onSuccess() {
    window.location.replace(`${window.location.origin}/merchant-platform/#/dashboard`)
  },
})

// #短信验证
const { mutate: mutateSmsVerify, isLoading } = useMutation({
  mutationFn: LoginPwdTfaSmsVerify,
  async onSuccess(res) {
    const { accessToken } = res
    if (!accessToken)
      return

    const deviceId = await getDeviceCode()
    sessionStorage.setItem('accessToken', accessToken)
    mutateLogonAsync({
      deviceId,
      appId: ZF_NO,
      accessToken,
    }).catch(() => undefined)
  },
})

async function handleSubmit() {
  await formRef.value?.validate()
  mutateSmsVerify({
    smsVerifyCode: formModel.code as string,
    smsVerifyTicket: resendSmsData.value?.smsVerifyTicket || props.data.smsVerifyTicket,
    twoFactorAuthTicket: props.data.twoFactorAuthTicket,
  })
}

function handleCancel() {
  visible.value = false
  formRef.value?.resetFields()
}

defineExpose({
  open() {
    visible.value = true
    nextTick(() => smsInputRef.value?.startCountdown())
  },
})
</script>

<template>
  <el-dialog
    v-model="visible" :close-on-press-escape="false" :close-on-click-modal="false" append-to-body title="身份验证"
    :width="500" @close="handleCancel"
  >
    <p class=" mb-5 text-n-800 text-sm">
      您已开启双重验证。为了账号安全，请输入验证码。
    </p>
    <el-form ref="formRef" :model="formModel" :rules="formRules" label-position="top" @submit.prevent="handleSubmit">
      <el-form-item prop="code" class="w-full">
        <template #label>
          <div class="flex-inline">
            {{ props.data?.mobileNoMask }}

            <el-popover :content="isAdmin ? '请联系商户管理员修改' : '请联系客服修改'" placement="top-start">
              <template #reference>
                <el-button type="primary" link size="small" class="ml-2">
                  手机号无法使用？
                </el-button>
              </template>
            </el-popover>
          </div>
        </template>

        <SpInputVerificationCode
          ref="smsInputRef"
          v-model="formModel.code"
          placeholder="请输入"
          @click-send="handleClickSend"
        />
      </el-form-item>

      <el-button :loading="isLoading" type="primary" class="w-full mt-4" size="large" @click="handleSubmit">
        确认
      </el-button>
    </el-form>
  </el-dialog>
</template>

<style scoped>
:deep(.inputVerification-container) {
  width: 100%;
  display: flex;
  gap: 10px;
}
</style>
